import { useContext } from 'react';
import { Card, Col, Row, Spin, Table } from 'antd';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import { IDashboardInvoicesImportHistoryItem } from '../../../../services/http/mappers/DashboardMapper';
import { ThemeContext } from '../../../../contexts/ThemeContext';

interface InvoicesImportHistoryChartProps {
    data: IDashboardInvoicesImportHistoryItem[];
    isLoading: boolean;
    showTable?: boolean;
    showStatsCards?: boolean;
    showLabels?: boolean;
    chartHeight?: number;
}

const formatNumber = (value: number) => new Intl.NumberFormat('pt-BR').format(value);
const formatMonthLabel = (label: string) =>
    /^\d{2}\/\d{4}$/.test(label) ? `${label.slice(0, 3)}${label.slice(5)}` : label;

const formatFullMonthLabel = (label: string) => {
    const match = /^(\d{2})\/(\d{4})$/.exec(label);
    if (!match) return label;
    const [, month, year] = match;
    const date = new Date(Number(year), Number(month) - 1, 1);
    const formatted = date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

const CHART_COLORS = { imported: '#5b8ff9', valid: '#5ad8a6' };

const renderColoredNumber = (color: string) => (value: number) => (
    <span style={{ color }}>{formatNumber(value)}</span>
);

const historyTableColumns = [
    { title: 'Mês', dataIndex: 'label', key: 'label', render: formatFullMonthLabel },
    {
        title: <span style={{ color: CHART_COLORS.imported }}>Importadas</span>,
        dataIndex: 'total',
        key: 'total',
        align: 'right' as const,
        render: renderColoredNumber(CHART_COLORS.imported),
    },
    {
        title: <span style={{ color: CHART_COLORS.valid }}>Válidas</span>,
        dataIndex: 'totalValid',
        key: 'totalValid',
        align: 'right' as const,
        render: renderColoredNumber(CHART_COLORS.valid),
    },
    {
        title: '% Válidas',
        key: 'validPercentage',
        align: 'right' as const,
        render: (_: unknown, item: IDashboardInvoicesImportHistoryItem) =>
            item.total > 0 ? `${((item.totalValid / item.total) * 100).toFixed(1)}%` : '0%',
    },
];

export default function InvoicesImportHistoryChart({
    data,
    isLoading,
    showTable,
    showStatsCards,
    showLabels = false,
    chartHeight = 300,
}: InvoicesImportHistoryChartProps) {
    const { theme } = useContext(ThemeContext);
    const totalImported = data.reduce((acc, item) => acc + item.total, 0);
    const totalValid = data.reduce((acc, item) => acc + item.totalValid, 0);
    const validPercentage = totalImported > 0 ? ((totalValid / totalImported) * 100).toFixed(1) : '0';
    const averageMonthly = data.length > 0 ? Math.round(totalImported / data.length) : 0;

    return (
        <Card
            title={
                <div style={{ paddingTop: 16 }}>
                    <div style={{ fontWeight: 700, fontSize: 20 }}>Histórico de notas importadas</div>
                    <div style={{ fontWeight: 400, fontSize: 16, color: '#888' }}>Últimos 12 meses</div>
                </div>
            }
            extra={
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '4px 12px',
                    borderRadius: 20,
                    border: `1px solid ${theme === 'light' ? '#FFFFFF' : '#274916'}`,
                    background: theme === 'light' ? '#FFFFFF' : '#162312',
                    fontSize: 18,
                    fontWeight: 600,
                    color: '#52c41a',
                }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#52c41a', display: 'inline-block' }} />
                    {validPercentage}% válidas
                </div>
            }
            style={{ background: theme === 'light' ? '#FFFFFF' : '#1f1f1f', borderRadius: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: 'none', marginTop: 0, flex: 1 }}
            bodyStyle={{ padding: 20 }}
        >
            {isLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: chartHeight }}>
                    <Spin />
                </div>
            ) : (
                <ResponsiveContainer width="100%" height={chartHeight} style={{ overflow: 'visible' }}>
                    <BarChart data={data} margin={{ top: 24, right: 8, left: 0, bottom: 0 }} barSize={18} barGap={4}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'light' ? '#d0d0d0' : '#303030'} />
                        <XAxis dataKey="label" axisLine={false} tickLine={false} style={{ fontSize: 14 }} tick={{ fill: theme === 'light' ? '#444' : '#aaa' }} tickFormatter={formatMonthLabel} />
                        <YAxis axisLine={false} tickLine={false} style={{ fontSize: 14 }} tick={{ fill: theme === 'light' ? '#444' : '#aaa' }} allowDecimals={false} />
                        <Tooltip
                            labelStyle={{
                                fontSize: 18
                            }}
                            wrapperStyle={{
                                fontSize: 16
                            }}
                            formatter={(value: number, name: string) => [formatNumber(value), name]}
                            contentStyle={theme === 'dark' ? { backgroundColor: '#1f1f1f', borderColor: '#303030', color: '#e8e8e8' } : undefined}
                            itemStyle={theme === 'dark' ? { color: '#e8e8e8' } : undefined}
                        />
                        <Legend wrapperStyle={{
                            fontSize: 14,
                            paddingTop: 8,
                        }} />
                        <Bar dataKey="total" name="Importadas" fill={CHART_COLORS.imported} radius={[4, 4, 0, 0]} animationDuration={600}>
                            {showLabels && (
                                <LabelList dataKey="total" position="top" formatter={(v: unknown) => formatNumber(v as number)} fill="#5b8ff9" style={{ fontSize: 9, fontWeight: 600 }} />
                            )}
                        </Bar>
                        <Bar dataKey="totalValid" name="Válidas" fill={CHART_COLORS.valid} radius={[4, 4, 0, 0]} animationDuration={600}>
                            {showLabels && (
                                <LabelList dataKey="totalValid" position="top" formatter={(v: unknown) => formatNumber(v as number)} fill="#5ad8a6" style={{ fontSize: 9, fontWeight: 600 }} />
                            )}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            )}

            {!isLoading && showStatsCards && (
                <Row gutter={[10, 10]} style={{ marginTop: 6 }}>
                    <Col span={6}>
                        <div style={{ background: '#ffffff', padding: '10px 12px', borderRadius: 4, border: '1px solid #e2e8f0', borderLeft: '4px solid #2563eb', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>
                                Total Importadas
                            </div>
                            <div style={{ fontSize: 15, fontWeight: 700, color: '#2563eb' }}>
                                {formatNumber(totalImported)}
                            </div>
                        </div>
                    </Col>
                    <Col span={6}>
                        <div style={{ background: '#ffffff', padding: '10px 12px', borderRadius: 4, border: '1px solid #e2e8f0', borderLeft: '4px solid #15803d', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>
                                Total Válidas
                            </div>
                            <div style={{ fontSize: 15, fontWeight: 700, color: '#15803d' }}>
                                {formatNumber(totalValid)}
                            </div>
                        </div>
                    </Col>
                    <Col span={6}>
                        <div style={{ background: '#ffffff', padding: '10px 12px', borderRadius: 4, border: '1px solid #e2e8f0', borderLeft: '4px solid #15803d', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>
                                Taxa de Validação
                            </div>
                            <div style={{ fontSize: 15, fontWeight: 700, color: '#15803d' }}>
                                {validPercentage}%
                            </div>
                        </div>
                    </Col>
                    <Col span={6}>
                        <div style={{ background: '#ffffff', padding: '10px 12px', borderRadius: 4, border: '1px solid #e2e8f0', borderLeft: '4px solid #0284c7', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>
                                Média / Mês
                            </div>
                            <div style={{ fontSize: 15, fontWeight: 700, color: '#0284c7' }}>
                                {formatNumber(averageMonthly)}
                            </div>
                        </div>
                    </Col>
                </Row>
            )}

            {!isLoading && showTable && (
                <Table
                    className="dashboard-table"
                    dataSource={data}
                    columns={historyTableColumns}
                    rowKey="label"
                    pagination={false}
                    size="small"
                    style={{ marginTop: 6 }}
                />
            )}
        </Card>
    );
}
